const _hostname = typeof window !== 'undefined' ? window.location.hostname : '';
const _origin = typeof window !== 'undefined' ? window.location.origin : '';

export const VIDEO_PROVIDERS = [
	{
		reg: /^(?:https?:\/\/)?(?:www\.|m\.)?(?:youtube\.com|youtu\.be)(?:\/|\?|$)/i,
		url: 'https://www.youtube.com/embed/',
		params: {
			enablejsapi: 1,
			origin: _origin,
		},
		resolve(url) {
			try {
				const urlObj = new URL(url);
				const hostname = urlObj.hostname.replace(/^(www\.|m\.)/, '');
				let id;

				if (hostname === 'youtu.be') {
					id = urlObj.pathname.slice(1).split('/')[0];
				} else if (urlObj.pathname.startsWith('/embed/')) {
					id = urlObj.pathname.split('/embed/')[1]?.split('/')[0];
				} else if (urlObj.pathname.startsWith('/v/')) {
					id = urlObj.pathname.split('/v/')[1]?.split('/')[0];
				} else {
					id = urlObj.searchParams.get('v');
				}

				return id ? `https://www.youtube.com/embed/${id}` : null;
			} catch {
				return null;
			}
		},
	},
	{
		reg: /^.*vimeo.com\/(\d+)($|\/|\b)/i,
		url: 'https://player.vimeo.com/video/$1',
		params: {
			title: 0,
			byline: 0,
			portrait: 0,
		},
	},
	{
		reg: /^.*(?:\/video|dai.ly)\/([A-Za-z0-9]+)([^#\&\?]*).*/i,
		url: 'https://www.dailymotion.com/embed/video/$1',
		params: {
			autoplay: 0,
		},
	},
	{
		reg: /^.*coub.com\/(?:embed|view)\/([A-Za-z0-9]+)([^#\&\?]*).*/i,
		url: 'https://coub.com/embed/$1',
		params: {
			autoplay: 0,
		},
	},
	{
		reg: /^(?:https?:\/\/)?(?:www\.)?twitch\.tv\/videos\/(\d+)/i,
		url: 'https://player.twitch.tv/?video=$1',
		params: {
			parent: _hostname,
			autoplay: false,
		},
	},
	{
		reg: /^(?:https?:\/\/)?(?:www\.)?twitch\.tv\/([A-Za-z0-9_]+)\/?$/i,
		url: 'https://player.twitch.tv/?channel=$1',
		params: {
			parent: _hostname,
			autoplay: false,
		},
	},
];

/**
 * Extracts the BigNerve video id from an uploaded-video attachment URL, e.g.
 * `https://api.../content/attachments/video/{id}/iframe` (or `/download`,
 * `/info`). Returns the id (which equals the file `key`) so a rendered embed
 * can be correlated to an in-progress upload in the video queue.
 *
 * @param {string} url - The attachment URL
 * @returns {string | null} The video id, or null if the URL isn't a BigNerve video attachment
 */
export const extractUploadedVideoId = (url) => {
	if (!url || typeof url !== 'string') {
		return null;
	}

	const match = url.match(/\/content\/attachments\/video\/([^/?#]+)/i);

	return match ? match[1] : null;
};

/**
 * Parses a URL and returns embed info if it matches a supported video provider.
 * Supports YouTube, Vimeo, Dailymotion, Coub, and Twitch.
 *
 * @param {string} url - The URL to parse
 * @param {object} [params] - Optional params to override provider defaults
 * @returns {{ src: string } | null} Embed src with params included, or null if not a video URL
 */
export const parseVideoUrl = (url, params = {}) => {
	if (!url || typeof url !== 'string') {
		return null;
	}
	for (let i = 0; i < VIDEO_PROVIDERS.length; i++) {
		const provider = VIDEO_PROVIDERS[i];
		const match = url.match(provider.reg);
		if (match) {
			let src;

			if (provider.resolve) {
				src = provider.resolve(url);
				if (!src) continue;
			} else {
				src = url.replace(provider.reg, provider.url);
			}

			const mergedParams = { ...provider.params, ...params };
			const query = new URLSearchParams(mergedParams).toString();

			return {
				src: query ? `${src}${src.includes('?') ? '&' : '?'}${query}` : src,
			};
		}
	}
	return null;
};
